import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../lib/baseQueries";
import {
  type IWorkspace,
  type IWorkspacePagination,
  type IDelegatedInvite,
  type IDelegatedInvitePreview,
} from "./Workspace.types";

export interface IGetWorkspacesParams {
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export const workspaceApi = createApi({
  reducerPath: "workspaceApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Workspace", "DelegatedInvite"],
  endpoints: (builder) => ({
    getAllWorkspaces: builder.query<
      IWorkspacePagination,
      IGetWorkspacesParams | void
    >({
      query: (params) => ({
        url: "/v1/workspaces",
        params: params || undefined,
      }),
      providesTags: ["Workspace"],
    }),

    getWorkspaceById: builder.query<
      { success: boolean; workspace: IWorkspace },
      string
    >({
      query: (id) => ({ url: `/v1/workspaces/${id}` }),
      providesTags: (result, error, id) => [{ type: "Workspace", id }],
    }),

    createWorkspace: builder.mutation<
      { success: boolean; workspace: IWorkspace },
      { code: string; state: string; scopes: string }
    >({
      query: ({ code, state, scopes }) => ({
        url: "/v1/workspaces",
        method: "POST",
        body: { code, state, scopes },
      }),
      invalidatesTags: ["Workspace"],
    }),

    updateWorkspace: builder.mutation<
      { success: boolean; workspace: IWorkspace },
      { id: string; data: Partial<IWorkspace> }
    >({
      query: ({ id, data }) => ({
        url: `/v1/workspaces/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Workspace" as const, id },
        "Workspace",
      ],
    }),

    deleteWorkspace: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/v1/workspaces/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Workspace"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { memberApi } = await import("../Member/Member.api");
          dispatch(memberApi.util.invalidateTags(["Member"]));
        } catch {}
      },
    }),

    leaveWorkspace: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/v1/workspaces/${id}/leave`,
        method: "POST",
      }),
      invalidatesTags: ["Workspace"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { memberApi } = await import("../Member/Member.api");
          dispatch(memberApi.util.invalidateTags(["Member"]));
        } catch {}
      },
    }),

    convertToPersonalWorkspace: builder.mutation<
      { message: string; workspace: IWorkspace },
      string
    >({
      query: (id) => ({
        url: `/v1/workspaces/${id}/convert-to-personal`,
        method: "POST",
      }),
      invalidatesTags: ["Workspace"],
    }),

    reconnectWorkspace: builder.mutation<
      { message: string; workspaceId: string },
      { workspaceId: string; code: string; state: string }
    >({
      query: ({ workspaceId, code, state }) => ({
        url: `/v1/workspaces/${workspaceId}/reconnect`,
        method: "POST",
        body: { code, state },
      }),
      invalidatesTags: ["Workspace"],
    }),

    checkConnectionStatus: builder.query<{ connectionStatus: string }, string>({
      query: (id) => ({ url: `/v1/oauth/connection-status/${id}` }),
    }),

    createDelegatedInvite: builder.mutation<
      IDelegatedInvite,
      { channelName?: string }
    >({
      query: (body) => ({
        url: "/v1/workspaces/delegated-invite",
        method: "POST",
        body,
      }),
      invalidatesTags: ["DelegatedInvite"],
    }),

    listDelegatedInvites: builder.query<IDelegatedInvite[], void>({
      query: () => ({
        url: "/v1/workspaces/delegated-invites",
      }),
      providesTags: ["DelegatedInvite"],
    }),

    revokeDelegatedInvite: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/v1/workspaces/delegated-invite/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DelegatedInvite"],
    }),

    getDelegatedInviteByToken: builder.query<IDelegatedInvitePreview, string>({
      query: (token) => ({
        url: `/v1/workspaces/delegated-invite/${token}`,
      }),
    }),

    claimDelegatedInvite: builder.mutation<
      { message: string; workspace: IWorkspace },
      { token: string; code: string; state: string }
    >({
      query: (body) => ({
        url: "/v1/workspaces/delegated-claim",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Workspace", "DelegatedInvite"],
    }),
  }),
});

export const {
  useGetAllWorkspacesQuery,
  useGetWorkspaceByIdQuery,
  useCreateWorkspaceMutation,
  useReconnectWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useDeleteWorkspaceMutation,
  useLeaveWorkspaceMutation,
  useConvertToPersonalWorkspaceMutation,
  useCheckConnectionStatusQuery,
  useCreateDelegatedInviteMutation,
  useListDelegatedInvitesQuery,
  useRevokeDelegatedInviteMutation,
  useGetDelegatedInviteByTokenQuery,
  useClaimDelegatedInviteMutation,
} = workspaceApi;
